import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { CloseIcon, DeleteIcon, EditIcon } from "@chakra-ui/icons";
import { createColumnHelper } from "@tanstack/react-table";
import { useCallback, useMemo, useRef, useState } from "react";
import WelcomeSection from "components/WelcomeSection";
import { ReactTable } from "components/ReactTable";
import { useTrackingParents, type TrackingParent, type ParentCreatePayload, type ParentUpdatePayload } from "hooks/useTrackingParents";
import { showToast, toastTypes } from "utils";

function formatName(p: TrackingParent) {
  const name = [p.first_name, p.last_name].filter(Boolean).join(" ");
  return name || p.username || "—";
}

export default function TrackingParentsPage() {
  const toast = useToast();
  const { parents, isLoading, error, create, update, remove } = useTrackingParents();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();
  const cancelRef = useRef<any>(null);

  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [editing, setEditing] = useState<TrackingParent | null>(null);
  const [formValues, setFormValues] = useState<ParentCreatePayload & ParentUpdatePayload>({
    user_id: 0,
    institution_id: null,
    sub_area_id: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [activeItem, setActiveItem] = useState<TrackingParent | null>(null);
  const [searchValue, setSearchValue] = useState("");

  const resetForm = useCallback(() => {
    setFormValues({ user_id: 0, institution_id: null, sub_area_id: null });
    setEditing(null);
    setFormMode("create");
  }, []);

  const openCreate = useCallback(() => {
    resetForm();
    setFormMode("create");
    onOpen();
  }, [onOpen, resetForm]);

  const openEdit = useCallback(
    (row: TrackingParent) => {
      setEditing(row);
      setFormMode("edit");
      setFormValues((prev) => ({
        ...prev,
        institution_id: row.institution_id ?? null,
        sub_area_id: row.sub_area_id ?? null,
      }));
      onOpen();
    },
    [onOpen]
  );

  const handleSubmit = useCallback(async () => {
    if (formMode === "create" && (!formValues.user_id || formValues.user_id < 1)) {
      showToast({ toast, message: "L'ID utilisateur est obligatoire.", status: toastTypes.error });
      return;
    }
    setIsSubmitting(true);
    try {
      if (formMode === "create") {
        await create({ user_id: formValues.user_id, institution_id: formValues.institution_id ?? undefined, sub_area_id: formValues.sub_area_id ?? undefined });
        showToast({ toast, message: "Parent créé.", status: toastTypes.success });
      } else if (editing) {
        await update(editing.id, { institution_id: formValues.institution_id ?? undefined, sub_area_id: formValues.sub_area_id ?? undefined });
        showToast({ toast, message: "Parent mis à jour.", status: toastTypes.success });
      }
      onClose();
      resetForm();
    } catch (e: any) {
      showToast({ toast, message: e?.response?.data?.detail || e?.message || "Erreur.", status: toastTypes.error });
    } finally {
      setIsSubmitting(false);
    }
  }, [create, update, editing, formMode, formValues, onClose, resetForm, toast]);

  const handleDeleteClick = useCallback((row: TrackingParent) => {
    setActiveItem(row);
    onConfirmOpen();
  }, [onConfirmOpen]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!activeItem) return;
    setDeletingId(activeItem.id);
    try {
      await remove(activeItem.id);
      showToast({ toast, message: "Parent supprimé.", status: toastTypes.success });
      onConfirmClose();
      setActiveItem(null);
    } catch (e: any) {
      showToast({ toast, message: e?.response?.data?.detail || "Erreur.", status: toastTypes.error });
    } finally {
      setDeletingId(null);
    }
  }, [activeItem, remove, toast, onConfirmClose]);

  const filteredData = useMemo(() => {
    const q = searchValue.trim().toLowerCase();
    if (!q) return parents;
    return parents.filter(
      (p) =>
        formatName(p).toLowerCase().includes(q) ||
        (p.email && p.email.toLowerCase().includes(q)) ||
        (p.username && p.username.toLowerCase().includes(q))
    );
  }, [parents, searchValue]);

  const columns = useMemo(() => {
    const col = createColumnHelper<TrackingParent>();
    return [
      col.accessor((r) => formatName(r), { id: "name", header: "Nom", cell: (info) => <Text>{info.getValue()}</Text> }),
      col.accessor("email", { header: "Email", cell: (info) => <Text fontSize="sm">{info.getValue() ?? "—"}</Text> }),
      col.accessor("phone_number", { header: "Téléphone", cell: (info) => <Text fontSize="sm">{info.getValue() ?? "—"}</Text> }),
      col.accessor("children_count", { header: "Enfants", cell: (info) => <Text>{info.getValue()}</Text> }),
      col.display({
        id: "action",
        header: "Action",
        cell: ({ row }) => (
          <HStack>
            <IconButton aria-label="Modifier" size="sm" icon={<EditIcon />} onClick={() => openEdit(row.original)} />
            <IconButton aria-label="Supprimer" size="sm" icon={<DeleteIcon />} colorScheme="red" onClick={() => handleDeleteClick(row.original)} isLoading={deletingId === row.original.id} />
          </HStack>
        ),
      }),
    ];
  }, [openEdit, handleDeleteClick, deletingId]);

  return (
    <>
      <Flex gap="18px" bg="very_light_gray" flex={1} flexDirection="column" py="20px">
        <WelcomeSection />
        <Flex flexDirection="column" alignItems="start" px={{ base: "20px", sm: "24px" }} minHeight="100vh">
          <Flex justifyContent="space-between" alignItems="center" w="100%" flexWrap="wrap" gap={4}>
            <Heading size="text2xl" as="h5" fontFamily="Arimo">Parents</Heading>
            <Button size="xl" variant="outline" colorScheme="red_origin" onClick={openCreate}>Créer un parent</Button>
          </Flex>
          {error && <Box mt={4} p={4} bg="red.50" borderRadius="md"><Text color="red.700">Erreur chargement.</Text></Box>}
          <Box mt={4} bg="white" w="100%" borderRadius="10px">
            <Flex p={4}><InputGroup w={{ base: "100%", sm: "300px" }}>
              <Input placeholder="Rechercher" value={searchValue} onChange={(e) => setSearchValue(e.target.value)} />
              <InputRightElement>{searchValue ? <IconButton aria-label="Effacer" size="sm" icon={<CloseIcon />} onClick={() => setSearchValue("")} /> : null}</InputRightElement>
            </InputGroup></Flex>
            {isLoading && !filteredData.length ? <Flex justify="center" py={10}><Spinner size="lg" /></Flex> : <ReactTable columns={columns as any} data={filteredData} size="sm" />}
            {!isLoading && !filteredData.length && <Box p={8} textAlign="center"><Text color="gray.600">Aucun parent.</Text></Box>}
          </Box>
        </Flex>
      </Flex>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{formMode === "create" ? "Ajouter un parent" : "Modifier le parent"}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              {formMode === "create" && (
                <FormControl isRequired>
                  <FormLabel>ID utilisateur (User)</FormLabel>
                  <Input type="number" value={formValues.user_id || ""} onChange={(e) => setFormValues((p) => ({ ...p, user_id: parseInt(e.target.value, 10) || 0 }))} placeholder="ID du compte" />
                </FormControl>
              )}
              <FormControl><FormLabel>Institution ID (optionnel)</FormLabel>
                <Input type="number" value={formValues.institution_id ?? ""} onChange={(e) => setFormValues((p) => ({ ...p, institution_id: e.target.value ? parseInt(e.target.value, 10) : null }))} />
              </FormControl>
              <FormControl><FormLabel>Zone (sub_area_id) (optionnel)</FormLabel>
                <Input type="number" value={formValues.sub_area_id ?? ""} onChange={(e) => setFormValues((p) => ({ ...p, sub_area_id: e.target.value ? parseInt(e.target.value, 10) : null }))} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>Annuler</Button>
            <Button colorScheme="blue" onClick={handleSubmit} isLoading={isSubmitting}>Enregistrer</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog isOpen={isConfirmOpen} leastDestructiveRef={cancelRef} onClose={onConfirmClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Supprimer le parent</AlertDialogHeader>
            <AlertDialogBody>Êtes-vous sûr de vouloir supprimer ce parent ?</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onConfirmClose}>Annuler</Button>
              <Button colorScheme="red" onClick={handleDeleteConfirm} isLoading={deletingId === activeItem?.id} ml={3}>Supprimer</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
